import moment from "moment";

/**
 * Centralized configuration for date and time format presets.
 *
 * This is the single source of truth for all date/time formatting options
 * that admins can select for their clients.
 */

export interface DateTimeFormatPreset {
  id: string;
  label: string;
  dateFormat: string;
  timeFormat: string;
  example: string; // Live example to show in UI
}

// Default formats (current system default)
export const DEFAULT_DATE_FORMAT = "DD/MM/YYYY";
export const DEFAULT_TIME_FORMAT = "HH:mm";

/**
 * Generate live example using current timestamp
 */
const generateExample = (dateFormat: string, timeFormat: string): string => {
  const now = moment();
  return `${now.format(dateFormat)} ${now.format(timeFormat)}`;
};

/**
 * Predefined date/time format presets
 * Admins can select from these options
 */
export const dateTimeFormatPresets: DateTimeFormatPreset[] = [
  {
    id: "eu_24",
    label: "dateTimeFormats.eu24",
    dateFormat: "DD/MM/YYYY",
    timeFormat: "HH:mm",
    example: generateExample("DD/MM/YYYY", "HH:mm"), // 31/12/2024 23:30
  },
  {
    id: "us_12",
    label: "dateTimeFormats.us12",
    dateFormat: "MM/DD/YYYY",
    timeFormat: "hh:mm A",
    example: generateExample("MM/DD/YYYY", "hh:mm A"), // 12/31/2024 11:30 PM
  },
  {
    id: "iso_24",
    label: "dateTimeFormats.iso24",
    dateFormat: "YYYY-MM-DD",
    timeFormat: "HH:mm",
    example: generateExample("YYYY-MM-DD", "HH:mm"), // 2024-12-31 23:30
  },
  {
    id: "eu_12",
    label: "dateTimeFormats.eu12",
    dateFormat: "DD/MM/YYYY",
    timeFormat: "hh:mm A",
    example: generateExample("DD/MM/YYYY", "hh:mm A"), // 31/12/2024 11:30 PM
  },
  {
    id: "us_24",
    label: "dateTimeFormats.us24",
    dateFormat: "MM/DD/YYYY",
    timeFormat: "HH:mm",
    example: generateExample("MM/DD/YYYY", "HH:mm"), // 12/31/2024 23:30
  },
  {
    id: "iso_12",
    label: "dateTimeFormats.iso12",
    dateFormat: "YYYY-MM-DD",
    timeFormat: "hh:mm A",
    example: generateExample("YYYY-MM-DD", "hh:mm A"), // 2024-12-31 11:30 PM
  },
  {
    id: "uk_24",
    label: "dateTimeFormats.uk24",
    dateFormat: "DD-MM-YYYY",
    timeFormat: "HH:mm",
    example: generateExample("DD-MM-YYYY", "HH:mm"), // 31-12-2024 23:30
  },
  {
    id: "us_slash_12",
    label: "dateTimeFormats.usSlash12",
    dateFormat: "MM/DD/YY",
    timeFormat: "hh:mm A",
    example: generateExample("MM/DD/YY", "hh:mm A"), // 12/31/24 11:30 PM
  },
];

/**
 * Get format preset by ID
 */
export const getFormatPresetById = (id: string): DateTimeFormatPreset | undefined => {
  return dateTimeFormatPresets.find((preset) => preset.id === id);
};

/**
 * Get format preset by format strings
 */
export const getFormatPreset = (
  dateFormat: string,
  timeFormat: string
): DateTimeFormatPreset | undefined => {
  return dateTimeFormatPresets.find(
    (preset) =>
      preset.dateFormat === dateFormat && preset.timeFormat === timeFormat
  );
};

/**
 * Get current selected format or default
 */
export const getCurrentFormat = (
  dateFormat?: string,
  timeFormat?: string
): { dateFormat: string; timeFormat: string } => {
  return {
    dateFormat: dateFormat || DEFAULT_DATE_FORMAT,
    timeFormat: timeFormat || DEFAULT_TIME_FORMAT,
  };
};
